import { useEffect, useState, createContext, useContext } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { PlaySquare, Calculator, User, LogOut, Loader2 } from "lucide-react";
import memberApi from "@/lib/memberApi";

const MemberContext = createContext(null);
export const useMember = () => useContext(MemberContext);

const NAV = [
  { to: "/community", label: "ספריית סרטונים", icon: PlaySquare, testId: "member-nav-library" },
  { to: "/community/calculators", label: "המחשבונים שלי", icon: Calculator, testId: "member-nav-calc" },
  { to: "/community/account", label: "החשבון שלי", icon: User, testId: "member-nav-account" },
];

export default function MemberLayout({ children, hideNav = false }) {
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await memberApi.get("/community/me");
        setMe(data);
      } catch (err) {
        navigate("/community/access-denied", { replace: true });
      } finally {
        setChecking(false);
      }
    })();
  }, [navigate]);

  const logout = async () => {
    try { await memberApi.post("/community/logout"); } catch (_) {}
    navigate("/community/access-denied", { replace: true });
  };

  if (checking) {
    return (
      <div dir="rtl" className="min-h-screen bg-stone-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
      </div>
    );
  }

  return (
    <MemberContext.Provider value={{ me, logout }}>
      <div dir="rtl" className="min-h-screen bg-stone-950 text-stone-50 font-sans">
        {/* Desktop sidebar (RTL: sticks to the right side) */}
        {!hideNav && (
          <aside className="hidden md:flex fixed right-0 top-0 bottom-0 w-64 bg-stone-900 border-l border-stone-800 flex-col z-40" data-testid="member-sidebar">
            <div className="px-6 py-6 border-b border-stone-800">
              <div className="text-xs font-bold text-amber-600 tracking-widest uppercase mb-1">אזור החברים</div>
              <div className="font-[Rubik] font-black text-lg text-stone-50 truncate">{me?.name || "חבר קהילה"}</div>
              <div className="text-xs text-stone-400 truncate mt-0.5">{me?.email}</div>
            </div>
            <nav className="flex-1 px-3 py-6 flex flex-col gap-1">
              {NAV.map((n) => {
                const Icon = n.icon;
                const active = n.to === "/community" ? pathname === n.to : pathname.startsWith(n.to);
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={n.testId}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                      active
                        ? "bg-amber-600/15 text-amber-500 font-bold"
                        : "text-stone-300 hover:bg-stone-800 hover:text-stone-50"
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">{n.label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="px-3 py-4 border-t border-stone-800">
              <button
                onClick={logout}
                data-testid="member-logout-btn"
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-stone-400 hover:bg-red-950/50 hover:text-red-400 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm">התנתקות</span>
              </button>
            </div>
          </aside>
        )}

        {/* Mobile bottom tab bar */}
        {!hideNav && (
          <nav className="md:hidden fixed bottom-0 inset-x-0 h-16 bg-stone-900/95 backdrop-blur-xl border-t border-stone-800 flex z-50" data-testid="member-mobile-nav">
            {NAV.map((n) => {
              const Icon = n.icon;
              const active = n.to === "/community" ? pathname === n.to : pathname.startsWith(n.to);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`${n.testId}-mobile`}
                  className={`flex-1 flex flex-col items-center justify-center gap-0.5 ${active ? "text-amber-500" : "text-stone-400"}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[10px] font-medium">{n.label}</span>
                </Link>
              );
            })}
          </nav>
        )}

        <main className={`${!hideNav ? "md:pr-64 pb-20 md:pb-0" : ""} min-h-screen`}>{children}</main>
      </div>
    </MemberContext.Provider>
  );
}
